/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { NavigationItem } from '../widgets/WebHeaderView/types';

const normalize = (p: string): string => (p.endsWith('/') ? p : p + '/');

type NavigationCandidate = {
  item: NavigationItem;
  host: string;
  pathname: string;
  pathnameLength: number;
};

const getUrlParts = (
  url: string,
): { host: string; pathname: string } | null => {
  try {
    const u = new URL(url);
    return { host: u.host, pathname: normalize(u.pathname).toLowerCase() };
  } catch {
    return null;
  }
};

function toNavigationCandidate(
  item: NavigationItem,
): NavigationCandidate | null {
  const parts = getUrlParts(item.redirectUrl);
  if (!parts) return null;

  return {
    item,
    host: parts.host,
    pathname: parts.pathname,
    pathnameLength: parts.pathname.length,
  };
}

// FE has a preprod env on ppairasia.com but BE does not, so nav items in
// preprod still point at airasia.com. Treat the two as the same host.
// Also strip leading `www.` so apex and www variants collapse.
const normalizeHost = (host: string): string =>
  host.replace(/^www\./, '').replace(/(^|\.)ppairasia\.com$/, '$1airasia.com');

const findSimpleMatches = (
  items: NavigationItem[],
  current: { host: string; pathname: string },
): NavigationCandidate[] => {
  const normalizedCurrentHost = normalizeHost(current.host);

  return items
    .map(toNavigationCandidate)
    .filter((c): c is NavigationCandidate => c !== null)
    .filter(({ host, pathname }) => {
      if (normalizeHost(host) !== normalizedCurrentHost) return false;
      // A bare '/' would otherwise match every URL on the host. Require
      // exact match so it only highlights on the literal root.
      if (pathname === '/') return current.pathname === '/';
      return current.pathname.startsWith(pathname);
    });
};

const findBestMatch = (
  candidates: NavigationCandidate[],
): NavigationCandidate | null =>
  candidates.reduce<NavigationCandidate | null>((best, candidate) => {
    if (!best) return candidate;
    return candidate.pathnameLength > best.pathnameLength ? candidate : best;
  }, null);

export const getSelectedNavigationItemId = (
  items: NavigationItem[],
  currentUrl: string,
): string | null => {
  const current = getUrlParts(currentUrl);
  if (!current) return null;

  const matches = findSimpleMatches(items, current);
  const winner = matches.length <= 1 ? matches[0] : findBestMatch(matches);
  return winner?.item.id ?? null;
};
